<script lang="ts">
  import { CATEGORY_META, type Card, type Category, type Track } from '@banger/shared';
  import { send, lastError } from '../lib/connection';
  import { autoMarkCopy, markPromptCopy } from '../lib/trackCopy';
  import CardGrid from './CardGrid.svelte';

  let { category, card, marked, canMark, markedThisRound, round, secs, cutoff = 2000, track = null, outline = [] }: {
    cutoff?: number | null;
    /** Album track (null in Single); Track 3 makes every empty square wild. */
    track?: Track | null;
    /** Track 2 pattern cells to outline. */
    outline?: readonly number[];
    category: Category;
    card: Card;
    marked: boolean[];
    canMark: number[];
    markedThisRound: number | null;
    round: number;
    secs: number;
  } = $props();

  const meta = $derived(CATEGORY_META[category]);
  const wild = $derived(track === 3);
  const copy = $derived(markPromptCopy(track, category));
  // Optimistic: the tapped square fills at once; the next view confirms it. An error, or no
  // confirmation within 1.5 s (a lost send), brings the choice back.
  let pending = $state<number | null>(null);
  let sentAt = $state(0);
  let now = $state(Date.now());
  $effect(() => {
    if (!sentAt) return;
    const t = setTimeout(() => (now = Date.now()), 1600);
    return () => clearTimeout(t);
  });
  $effect(() => { if ($lastError) { pending = null; sentAt = 0; } });
  $effect(() => { if (markedThisRound !== null) { pending = null; sentAt = 0; } });
  const shownPending = $derived(pending !== null && now - sentAt < 1500 ? pending : null);

  function pick(cell: number) {
    if (shownPending !== null) return;
    pending = cell;
    sentAt = Date.now();
    now = sentAt;
    send({ type: 'mark', round, cell });
    navigator.vibrate?.(30);
  }

  const done = $derived(markedThisRound !== null || shownPending !== null);
</script>

<section style="--c:{wild ? '#fff' : meta.color}">
  {#if done}
    <p class="head">Marked! ✓</p>
  {:else}
    <p class="head">{copy.before}<span class="col">{copy.word}</span>{copy.after}</p>
    <p class="sub">{autoMarkCopy(track)} in {secs}s</p>
  {/if}
  <CardGrid
    {card}
    {marked}
    eligible={done ? [] : canMark}
    pending={shownPending}
    fresh={markedThisRound}
    onpick={pick}
    {cutoff}
    {outline}
    {wild}
  />
</section>

<style>
  section { display: grid; gap: 10px; width: 100%; }
  .head { margin: 0; text-align: center; font-size: 1.6rem; font-weight: 900; }
  .col { color: var(--c); text-shadow: 0 0 12px var(--c); text-transform: uppercase; }
  .sub { margin: 0; text-align: center; opacity: 0.75; font-variant-numeric: tabular-nums; }
</style>
